import {
  Box,
  Divider,
  Icon,
  OsModal,
  SxProps,
  Typography,
} from "@outschool/backpack";
import { IconDefinition, faCheck } from "@outschool/icons";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

interface SearchToggleSheetOption<T> {
  key: T;
  label: string;
  isSelected: boolean;
  additionalSx?: SxProps;
}

interface SearchToggleSheetProps<T> {
  options: SearchToggleSheetOption<T>[];
  onSelect: (key: T) => void;
  onClear?: () => void;
  buttonLabel: string;
  modalTitle: string;
  clearLabel?: string;
  trackingName: string;
  buttonSx?: SxProps;
  containerSx?: SxProps;
  disableRipple?: boolean;
  buttonIcon?: IconDefinition;
  buttonIconPosition?: "left" | "right";
  children?: React.ReactNode;
  testId?: string;
}

/**
 * Generic sheet-based toggle selector component for LearnerSearchExperimentV2.
 * Displays a button that opens a bottom sheet with selectable options.
 */
export function SearchToggleSheet<T extends string>({
  options,
  onSelect,
  onClear,
  buttonLabel,
  modalTitle,
  clearLabel,
  trackingName,
  buttonSx,
  containerSx,
  disableRipple,
  buttonIcon = faCheck,
  buttonIconPosition = "left",
  children,
  testId,
}: SearchToggleSheetProps<T>) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = (key: T) => {
    onSelect(key);
    setIsOpen(false);
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      setIsOpen(false);
    }
  };

  const hasSelection = options.some(option => option.isSelected);

  return (
    <>
      <Box
        sx={{
          ...containerSx,
        }}
      >
        <TrackedButton
          variant="contained"
          size="small"
          onClick={() => setIsOpen(true)}
          disableRipple={disableRipple}
          disableElevation
          startIcon={
            buttonIconPosition === "left" ? (
              <Icon icon={buttonIcon} sx={{ fontSize: "1.1em" }} />
            ) : undefined
          }
          endIcon={
            buttonIconPosition === "right" ? (
              <Icon icon={buttonIcon} sx={{ fontSize: "1.1em" }} />
            ) : undefined
          }
          sx={{
            boxShadow: "none",
            fontWeight: "500",
            fontSize: isMobile ? "14px" : "16px",
            color: "white",
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
            paddingX: "0.75em",
            height: "32px",
            ...buttonSx,
          }}
          data-test-id={testId}
          trackingName={`${trackingName}_toggle`}
          trackingSharedProperties={{
            button_label: buttonLabel,
            is_opening_sheet: true,
          }}
        >
          <Typography variant="inherit">{buttonLabel}</Typography>
        </TrackedButton>
      </Box>

      <OsModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={modalTitle}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75em",
          }}
          data-test-id={testId ? `${testId}-menu` : undefined}
        >
          {onClear && hasSelection && clearLabel && (
            <TrackedButton
              variant="outlined"
              size="large"
              onClick={handleClear}
              trackingName={trackingName}
              trackingSharedProperties={{
                toggleName: "clear",
              }}
              sx={{
                borderRadius: "0.5em",
                fontWeight: "500",
                fontSize: "16px",
                justifyContent: "flex-start",
              }}
            >
              {clearLabel}
            </TrackedButton>
          )}
          {options.map(option => (
            <TrackedButton
              key={option.key}
              variant={option.isSelected ? "contained" : "outlined"}
              size="large"
              onClick={() => handleSelect(option.key)}
              trackingName={trackingName}
              trackingSharedProperties={{
                toggleName: option.key,
              }}
              data-test-id={
                testId ? `${testId}-option-${option.key}` : undefined
              }
              sx={{
                borderRadius: "0.5em",
                fontWeight: "500",
                fontSize: "16px",
                justifyContent: "flex-start",
                ...(option.isSelected && {
                  backgroundColor: "primary.main",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }),
                ...option.additionalSx,
              }}
            >
              {option.label}
            </TrackedButton>
          ))}
          {children && (
            <>
              <Divider sx={{ marginY: "0.5em" }} />
              {children}
            </>
          )}
        </Box>
      </OsModal>
    </>
  );
}
